"use client";

import React from "react";
import { usePathname } from "next/navigation";
import { useAuth } from "@/context/auth-context";
import {
    hasRecoverableAuthHints,
    isStandaloneDisplayRoute,
    isProtectedAppRoute,
    readAuthHintSnapshot,
} from "@/components/auth/authRecovery";

type AuthContextValue = ReturnType<typeof useAuth>;
type StableUser =
    | AuthContextValue["userProfile"]
    | AuthContextValue["externalUserProfile"]
    | null;

export const useRecoverableProtectedAuth = () => {
    const auth = useAuth();
    const readPathname = usePathname as unknown as
        | (() => string | null)
        | undefined;
    const pathname = readPathname?.() ?? "";
    const inferredLoginType =
        auth.loginType ??
        (auth.userProfile
            ? "msal"
            : auth.externalUserProfile
              ? "external"
              : null);
    const currentUser =
        inferredLoginType === "msal"
            ? auth.userProfile
            : inferredLoginType === "external"
              ? auth.externalUserProfile
              : (auth.userProfile ?? auth.externalUserProfile);

    const stableCurrentUserRef = React.useRef<StableUser>(null);
    const stableLoginTypeRef =
        React.useRef<AuthContextValue["loginType"]>(null);

    const authHintSnapshot = readAuthHintSnapshot(
        typeof window === "undefined" ? null : window.localStorage,
        false
    );

    const hasRecoverableProtectedSession =
        (isProtectedAppRoute(pathname) || isStandaloneDisplayRoute(pathname)) &&
        hasRecoverableAuthHints(authHintSnapshot);

    React.useEffect(() => {
        if (auth.isAuthenticated && currentUser) {
            stableCurrentUserRef.current = currentUser;
            stableLoginTypeRef.current = inferredLoginType;
        }
    }, [auth.isAuthenticated, inferredLoginType, currentUser]);

    const stableCurrentUser =
        currentUser ??
        (hasRecoverableProtectedSession ? stableCurrentUserRef.current : null);
    const stableLoginType =
        inferredLoginType ??
        (hasRecoverableProtectedSession ? stableLoginTypeRef.current : null);
    const stableUserProfile =
        stableLoginType === "msal"
            ? (stableCurrentUser as AuthContextValue["userProfile"])
            : null;
    const stableExternalUserProfile =
        stableLoginType === "external"
            ? (stableCurrentUser as AuthContextValue["externalUserProfile"])
            : null;
    const stableIsAuthenticated =
        auth.isAuthenticated ||
        (hasRecoverableProtectedSession && !!stableCurrentUser);

    return {
        ...auth,
        userProfile: stableUserProfile,
        externalUserProfile: stableExternalUserProfile,
        currentUser: stableCurrentUser,
        loginType: stableLoginType,
        isAuthenticated: stableIsAuthenticated,
        hasRecoverableProtectedSession,
    };
};
